/**
 * PromoterDashboardPage.tsx - Main Promoter Dashboard Interface
 *
 * This component provides the complete promoter dashboard experience with
 * performance metrics, fundraiser details, and promotional tools.
 *
 * Key Features:
 * - Comprehensive promoter performance dashboard
 * - Individual promoter statistics and analytics
 * - Fundraiser details and progress tracking
 * - Promotional tools and sharing capabilities
 * - Reward tracking and achievement display
 * - Responsive design for all device types
 *
 * Dashboard Sections:
 * - Header: Promoter info and key metrics
 * - Stats: Performance statistics and progress
 * - Chart: Visual performance analytics
 * - Fundraiser Details: Campaign information and status
 * - Promoters: Leaderboard and peer comparison
 * - Rewards: Achievement tracking and reward status
 * - Warning Bar: Important notifications and alerts
 *
 * Performance Tracking:
 * - Total amount raised by promoter
 * - Number of donations attributed
 * - Goal progress and achievement status
 * - Ranking among other promoters
 * - Performance trends and analytics
 *
 * Props: None (uses URL parameters and MobX store)
 *
 * Dependencies: Dashboard components, MobX store, URL parameter parsing
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { ArrowLeft } from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { PromoterDonationsTable } from "../PromoterDonationsTable";
import { SocialShareModal } from "../SocialShareModal";
import { Toast } from "../Toast";
import {
  getExpirationBadgeText,
  shouldShowExpirationBadge,
  getPendingBadgeText,
  shouldShowPendingBadge,
} from "../../utils/badgesUtils";
import { PromoterDashboardHeader } from "./PromoterDashboardHeader";
import { PromoterDashboardWarningBar } from "./PromoterDashboardWarningBar";
import PromoterDashboardFundraiserDetails from "./PromoterDashboardFundraiserDetails";
import { PromoterDashboardStats } from "./PromoterDashboardStats";
import { PromoterDashboardChart } from "./PromoterDashboardChart";
import { PromoterDashboardPromoters } from "./PromoterDashboardPromoters";
import PromoterDashboardRewards from "./PromoterDashboardRewards";
import { EmbedFundraiserModal } from "../EmbedFundraiserModal";
import { FundRaiserStatus } from "../../enums";
import { FundraiserService } from "../../services";
import {
  AuthService,
  Loader,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { CreateFundraiserModel } from "../../models";
import { DailyDonationsChart } from "../fundraiser-details";
import { NavigateFunction } from "react-router-dom";
import ToastService from "../ToastService";

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #667eea 0%,
    #764ba2 25%,
    #f093fb 50%,
    #f5576c 75%,
    #4facfe 100%
  );
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(248, 250, 252, 0.95) 100%
    );
    pointer-events: none;
  }
`;

const BackToDashboard = styled.button`
  position: fixed;
  top: 24px;
  left: 24px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid #e2e8f0;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #ffffff;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }
`;

const HeaderWrapper = styled.div`
  width: 100%;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  padding: 0 24px 100px;
  min-height: 100vh;
`;

const ContentArea = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

interface PromoterDashboardPageProps {
  promoterId: string;
  fundraiserId: string;
  navigate: NavigateFunction;
  showBackButton?: boolean;
}

const PromoterDashboardPage = observer(
  ({ promoterId, fundraiserId, navigate, showBackButton }: PromoterDashboardPageProps) => {
    const [showSocialShareModal, setShowSocialShareModal] = useState(false);
    const [showLinksDropdown, setShowLinksDropdown] = useState(false);
    const [showEmbedModal, setShowEmbedModal] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [showLoader, setShowLoader] = useState(true);

    // Get promoter and campaign data
    // const promoter = fundraiserStore.getPromoterById(promoterId);
    // const fundraiser = fundraiserStore.fundraisers.find(f => f.id === fundraiserId);
    // const allPromoters = fundraiserStore.getFundraiserPromoters(fundraiserId);
    // const builderData = fundraiserStore.builderData || ({} as BuilderData);

    const fundraiser = fundraiserStore.selectedFundraiser;
    const allPromoters = fundraiser.leaderboard || [];
    const promoter = fundraiser?.promoterProgress;
    // Check fundraiser timing conditions
    const today = new Date();
    const startDate = fundraiser?.startDate
      ? new Date(fundraiser.startDate)
      : null;
    const endDate = fundraiser?.endDate ? new Date(fundraiser.endDate) : null;
    const isPending = startDate && startDate > today;
    const isActive = fundraiser?.status === FundRaiserStatus.Active;
    const hasEndDate = !!endDate;

    // Check if any promoter has raised money (not just direct donations)
    const anyPromoterHasMoney = allPromoters.some(
      (p) => p.totalRaisedAmount > 0
    );

    useEffect(() => {
      if (!showLoader) {
        setShowLoader(true);
      }

      if (!promoterId || !fundraiserId) {
        navigate("/fundraiser/dashboard");
        return;
      }
      FundraiserService.getPromotersDashboardData(
        promoterId,
        fundraiserId
      ).subscribe({
        next: (response) => {
          if (response.result) {
            const mappedData = CreateFundraiserModel.deserializeAPIModel(
              response.result
            );
            fundraiserStore.setFundraiserDetails(mappedData);
            setShowLoader(false);
          }
        },
        error: (error) => {
          ToastService.showError("Error fetching promoter dashboard data.");
        },
      });
    }, [fundraiserId, promoterId]);

    // Determine if we should show warning bar and what content
    const shouldShowWarningBar = () => {
      if (!isActive) return false;

      if (isPending) return true; // Always show pending message

      if (
        hasEndDate &&
        shouldShowExpirationBadge(
          endDate,
          fundraiser?.status || FundRaiserStatus.Active
        )
      ) {
        return true; // Always show expiration warning for active fundraisers
      }

      if (!hasEndDate) {
        return anyPromoterHasMoney; // Only show rank if promoters have money
      }

      return false;
    };

    const getWarningBarContent = () => {
      if (!isActive) return "";

      if (isPending && startDate) {
        return getPendingBadgeText(startDate)
          .replace("STARTS IN ", "")
          .toLowerCase()
          .replace(/^/, "Fundraiser starts in ");
      }

      if (
        hasEndDate &&
        endDate &&
        shouldShowExpirationBadge(
          endDate,
          fundraiser?.status || FundRaiserStatus.Active
        )
      ) {
        const now = currentTime;
        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );
        const endDateDay = new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate()
        );

        // Check if expiration is today - show hours and minutes with real-time updates
        if (endDateDay.getTime() === today.getTime()) {
          const timeToExpiration = endDate.getTime() - now.getTime();
          const hoursRemaining = Math.floor(
            timeToExpiration / (1000 * 60 * 60)
          );
          const minutesRemaining = Math.floor(
            (timeToExpiration % (1000 * 60 * 60)) / (1000 * 60)
          );

          let timeText = "";
          if (hoursRemaining > 0) {
            timeText = `${hoursRemaining} hour${hoursRemaining !== 1 ? "s" : ""} ${minutesRemaining} minute${minutesRemaining !== 1 ? "s" : ""}`;
          } else if (minutesRemaining > 0) {
            timeText = `${minutesRemaining} minute${minutesRemaining !== 1 ? "s" : ""}`;
          } else {
            timeText = "less than 1 minute";
          }

          if (anyPromoterHasMoney) {
            return `Fundraiser expires in ${timeText}. You are currently in #${promoter?.rank} position.`;
          }
          return `Fundraiser expires in ${timeText}.`;
        } else {
          // For other days, show days remaining
          const timeToExpiration = endDate.getTime() - now.getTime();
          const daysToExpiration = Math.ceil(
            timeToExpiration / (1000 * 60 * 60 * 24)
          );
          const timeText = `${daysToExpiration} day${daysToExpiration !== 1 ? "s" : ""}`;

          if (anyPromoterHasMoney) {
            return `Fundraiser expires in ${timeText}. You are currently in #${promoter?.rank} position.`;
          }
          return `Fundraiser expires in ${timeText}.`;
        }
      }

      if (!hasEndDate && anyPromoterHasMoney) {
        return `You are currently in #${promoter?.rank} position.`;
      }

      return "";
    };

    const copyDonateLink = async () => {
      const donateLink = fundraiserStore.getFundraiserPublicLinkFromModel(
        fundraiser,
        promoterId
      );
      try {
        await navigator.clipboard.writeText(donateLink);
        ToastService.showSuccess("Donation page link copied to clipboard");
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = donateLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        ToastService.showSuccess("Donation page link copied to clipboard");
      }
      setShowLinksDropdown(false);
    };

    const copyInviteLink = async () => {
      const inviteLink =
        fundraiserStore.getFundRaiserDomainBaseURL() +
        `/fundraiser/promoter-invite/${fundraiserId}?PID=${promoterId}`;

      try {
        await navigator.clipboard.writeText(inviteLink);
        ToastService.showSuccess("Promoter invite link copied to clipboard");
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = inviteLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        ToastService.showSuccess("Promoter invite link copied to clipboard");
      }
      setShowLinksDropdown(false);
    };

    const copyGalleryLink = async () => {
      const galleryLink = fundraiser.galleryPageName
        ? fundraiserStore.getPublicGalleryLinkByName(
            fundraiser.shortUniqueId,
            fundraiser.galleryPageName,
            promoterId
          )
        : fundraiserStore.getGalleryLink(
            fundraiser.galleryId || "",
            promoterId
          );
      try {
        await navigator.clipboard.writeText(galleryLink);
        ToastService.showSuccess("Gallery link copied to clipboard");
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = galleryLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        ToastService.showSuccess("Gallery link copied to clipboard");
      }
      setShowLinksDropdown(false);
    };

    const handleViewEmbed = () => {
      // Open the Embed modal directly for this fundraiser
      setShowEmbedModal(true);
      setShowLinksDropdown(false);
    };

    const handleLinksClick = () => {
      setShowLinksDropdown(!showLinksDropdown);
    };

    const handleInviteClick = () => {
      fundraiserStore.setSelectedFundraiser(fundraiserId);
      navigate(`/fundraiser/promoter-invite/${fundraiserId}`);
    };

    const handleShareClick = () => {
      setShowSocialShareModal(true);
    };

    // Real-time updates for same-day expiration
    useEffect(() => {
      if (
        hasEndDate &&
        endDate &&
        shouldShowExpirationBadge(
          endDate,
          fundraiser?.status || FundRaiserStatus.Active
        )
      ) {
        const now = new Date();
        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );
        const endDateDay = new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate()
        );

        // Only update in real-time if expiration is today
        if (endDateDay.getTime() === today.getTime()) {
          const interval = setInterval(() => {
            setCurrentTime(new Date());
          }, 60000); // Update every minute

          return () => clearInterval(interval);
        }
      }
    }, [hasEndDate, endDate, fundraiser?.status]);

    // Scroll detection
    useEffect(() => {
      const handleScroll = () => {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        setIsScrolled(scrollTop > 50);
      };

      window.addEventListener("scroll", handleScroll, { passive: true });

      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }, []);

    const handleImageError = () => {
      setImageError(true);
    };

    if (showLoader) {
      return <>{new Loader(true).loaderComponent}</>;
    }

    if (!promoter || !fundraiser) {
      return (
        <DashboardContainer>
          <ContentWrapper>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "50vh",
                textAlign: "center",
                color: "#64748b",
              }}
            >
              <h2>Promoter Not Found</h2>
              <p>The requested promoter or fundraiser could not be found.</p>
            </div>
          </ContentWrapper>
        </DashboardContainer>
      );
    }
    return (
      <DashboardContainer>
        {showBackButton && <BackToDashboard
          onClick={() => {
            fundraiserStore.setSelectedPromoter(null);
            navigate("/fundraiser/fundraiser-details");
          }}
        >
          <ArrowLeft size={20} color="#64748b" />
        </BackToDashboard>}

        <PromoterDashboardHeader
          promoter={promoter}
          fundraiser={fundraiser}
          isScrolled={isScrolled}
          onLinksClick={handleLinksClick}
          showLinksDropdown={showLinksDropdown}
          onDonateClick={() => {
            // Navigate to fundraiser page with promoter context
            fundraiserStore.setSelectedFundraiser(fundraiserId);
            navigate(`/fundraiser/${fundraiserId}?PID=${promoterId}`);
          }}
          onInviteClick={handleInviteClick}
          onShareClick={handleShareClick}
          onCopyDonateLink={copyDonateLink}
          onCopyInviteLink={copyInviteLink}
          onCopyGalleryLink={copyGalleryLink}
          onViewEmbed={handleViewEmbed}
          imageError={imageError}
          onImageError={handleImageError}
        />

        <ContentWrapper>
          <ContentArea>
            {shouldShowWarningBar() && (
              <PromoterDashboardWarningBar content={getWarningBarContent()} />
            )}

            <PromoterDashboardFundraiserDetails
              promoterId={promoterId}
              fundraiserId={fundraiserId}
              fundraiser={fundraiser}
            />

            <PromoterDashboardStats
              promoterId={promoterId}
              fundraiserId={fundraiserId}
              promoter={promoter}
              promoters={fundraiser.leaderboard || []}
              donations={fundraiser.donationsBoard || []}
            />

            <PromoterDashboardPromoters
              promoterId={promoterId}
              promoters={fundraiser.leaderboard || []}
            />

            <PromoterDashboardRewards
              promoterId={promoterId}
              fundraiser={fundraiser}
            />

            <DailyDonationsChart fundraiser={fundraiser} />

            {promoter &&
              fundraiser.donationsBoard &&
              fundraiser.donationsBoard.length > 0 && (
                <ContentCard>
                  <PromoterDonationsTable
                    promoter={fundraiser.promoterProgress}
                    donations={fundraiser.donationsBoard}
                    promoterId={promoterId}
                    fundraiserId={fundraiserId}
                    hideActions={true}
                    isPromoterDashboard={true}
                  />
                </ContentCard>
              )}
          </ContentArea>
        </ContentWrapper>

        <SocialShareModal
          gallerySettings={fundraiserStore.gallerySettings}
          isVisible={showSocialShareModal}
          onClose={() => setShowSocialShareModal(false)}
          shortUniqueId={AuthService.shortUniqueId}
        />

        {fundraiser && (
          <EmbedFundraiserModal
            isVisible={showEmbedModal}
            onClose={() => setShowEmbedModal(false)}
          />
        )}

        {!fundraiser && (
          <EmbedFundraiserModal
            isVisible={showEmbedModal}
            onClose={() => setShowEmbedModal(false)}
          />
        )}
      </DashboardContainer>
    );
  }
);
export default withRouter(PromoterDashboardPage);
